import type { ActExplanation, ActExplanationSection } from "../types/adventure";

const brainSection: ActExplanationSection = {
  heading: "Ons brein werkt vaak precies zo",
  blocks: [
    {
      type: "paragraph",
      text: "De hele dag door stuurt je brein berichten naar je. Dat noemen we gedachten. Sommige gedachten zijn handig:",
    },
    {
      type: "helpfulExamples",
      items: ["Mijn fietsband is lek.", "Ik moet mijn huiswerk nog maken."],
    },
    {
      type: "paragraph",
      text: "Maar je brein stuurt ook andere berichten:",
    },
    {
      type: "unhelpfulExamples",
      items: [
        "Wat als het misgaat?",
        "Misschien kan ik dit niet.",
        "Straks lachen ze me uit.",
      ],
    },
  ],
};

const recognitionSection: ActExplanationSection = {
  heading: "Herken je dat?",
  blocks: [
    {
      type: "questions",
      items: [
        "Heb je soms zoveel gedachten dat je niet meer weet wat je moet kiezen?",
        "Heb je weleens gedachten die je tegenhouden om iets te proberen?",
        "Ben je soms zo druk in je hoofd dat je vergeet wat echt belangrijk voor je is?",
        "Heb je soms zoveel gedachtes dat je er gek van wordt?",
      ],
    },
  ],
};

function helpSection(closing: string): ActExplanationSection {
  return {
    heading: "Wat dan kan helpen",
    blocks: [
      {
        type: "paragraph",
        text: "Je kan gedachten niet stoppen. Het heeft ook niet zoveel zin om dat te proberen.",
      },
      {
        type: "paragraph",
        text: "Je kunt wel leren om gedachten meer te zien als berichten die binnenkomen. Sommige berichten zijn belangrijk. Andere berichten daar hoef je niks mee te doen. Je hoeft er niet mee in discussie te gaan en je hoeft ze ook niet te geloven. De gedachtes hoeven niet te bepalen wat jij doet.",
      },
      {
        type: "paragraph",
        text: "Dat noemen we in ACT: **defusie**.",
      },
      {
        type: "paragraph",
        text: closing,
      },
    ],
  };
}

function buildExplanation(intro: string, closing: string): ActExplanation {
  return {
    title: "Wat gebeurde er net?",
    cta: "Wil je het eens proberen?",
    sections: [
      { blocks: [{ type: "paragraph", text: intro }] },
      brainSection,
      recognitionSection,
      helpSection(closing),
    ],
  };
}

export const actExplanations: Record<string, ActExplanation> = {
  ruimtereis: buildExplanation(
    "Tijdens de missie kreeg je steeds meer berichten binnen — van het weer, de navigatie, de risicoscanner of je mede-reizigers. Sommige berichten waren handig. Andere berichten maakten je vooral onzeker, bang of aan het twijfelen. Op een gegeven moment waren er zóveel berichten dat het moeilijk werd om nog keuzes te maken.",
    "Wanneer je je gedachten minder serieus neemt en je focust op de gedachtes die je helpen om je missie te bereiken, heb je vaak minder last van je gedachtes en meer ruimte in je hoofd."
  ),
  jungle: buildExplanation(
    "Tijdens je expeditie door de jungle kreeg je steeds meer berichten binnen — van je kaart, kompas, buikgevoel of jungle-gids. Sommige berichten waren handig. Andere berichten maakten je vooral onzeker, bang of aan het twijfelen. Op een gegeven moment waren er zóveel berichten dat het moeilijk werd om nog keuzes te maken.",
    "Wanneer je je gedachten minder serieus neemt en je focust op de gedachtes die je helpen om de tempel te bereiken, heb je vaak minder last van je gedachtes en meer ruimte in je hoofd."
  ),
  roadtrip: buildExplanation(
    "Tijdens de reis kreeg je steeds meer berichten binnen — van de GPS, autoradio, verkeersinfo of je gezin op de achterbank. Sommige berichten waren handig. Andere berichten maakten je vooral onzeker, bang of aan het twijfelen. Op een gegeven moment waren er zóveel berichten dat het moeilijk werd om nog keuzes te maken.",
    "Wanneer je je gedachten minder serieus neemt en je focust op de gedachtes die je helpen om veilig op je bestemming aan te komen, heb je vaak minder last van je gedachtes en meer ruimte in je hoofd."
  ),
};

export function getActExplanation(adventureId: string): ActExplanation {
  return actExplanations[adventureId] ?? actExplanations.ruimtereis;
}
